export function normalizeInstallmentCount(value: number): number {
    if (!Number.isFinite(value)) return 2;
    return Math.max(2, Math.trunc(value));
}

export function normalizeCurrentInstallment(value: number, installmentCount: number): number {
    if (!Number.isFinite(value)) return 1;
    return Math.min(normalizeInstallmentCount(installmentCount), Math.max(1, Math.trunc(value)));
}

export function roundCurrency(value: number): number {
    return Math.round(value * 100) / 100;
}

/**
 * Estatísticas de um grupo de parcelas (compra parcelada) usadas pelo
 * Parcelamento pelo valor total: a MENOR parcela existente no banco (âncora —
 * não é sempre a nº 1: "a partir de 3/5" cadastra só da 3 em diante) e a soma
 * de todas as parcelas vivas do grupo.
 */
export interface InstallmentGroupStats {
    firstNumber: number;
    total: number;
}

type InstallmentRowLike = {
    installmentId?: string | null;
    installmentNumber?: number | null;
    amount: number;
};

/**
 * Colapsa parcelas para o Parcelamento pelo valor total (preferência — ver
 * ADR 0043): a parcela-âncora do grupo (a menor existente) passa a valer a soma
 * do grupo inteiro; as demais saem da leitura. Linhas sem installmentId passam
 * intactas. Grupo sem âncora no conjunto (ex.: só parcelas futuras num recorte
 * que não contém o mês da compra) é descartado por inteiro — a compra conta no
 * mês da âncora, que está fora do recorte. Espelho JS dos predicados SQL em
 * db/repositories/transactionScopes.ts — mudou lá, muda aqui.
 */
export function collapseInstallmentRows<T extends InstallmentRowLike>(
    rows: T[],
    statsByInstallmentId: Map<string, InstallmentGroupStats>,
): T[] {
    const result: T[] = [];
    for (const row of rows) {
        if (!row.installmentId) {
            result.push(row);
            continue;
        }
        const stats = statsByInstallmentId.get(row.installmentId);
        // Sem estatística do grupo não dá pra decidir âncora — mantém a linha
        // como está (defensivo; não deve acontecer com o batch do repositório).
        if (!stats) {
            result.push(row);
            continue;
        }
        if (row.installmentNumber === stats.firstNumber) {
            result.push({ ...row, amount: stats.total });
        }
    }
    return result;
}

export function getInstallmentAmountForNumber(
    totalAmount: number,
    installmentCount: number,
    installmentNumber: number,
): number {
    const count = normalizeInstallmentCount(installmentCount);
    const number = normalizeCurrentInstallment(installmentNumber, count);
    const baseAmount = roundCurrency(totalAmount / count);

    if (number === count) {
        return roundCurrency(totalAmount - baseAmount * (count - 1));
    }

    return baseAmount;
}
